import React, { useEffect, ReactNode } from 'react';
import styled from 'styled-components';
import { size } from 'lodash';
import { ContextMenuTrigger } from 'react-contextmenu';
import GenericTree from 'components/GenericTree';
import { Path } from 'components/GenericTree/types';
import { ApiItem, TreeItem as TreeItemType } from './types';
import useTree from './useTree';
import TreeItem from './TreeItem';
import { TreeActionsProvider } from './TreeActionsContext';
import { normalize } from './utils';
import EmptySpaceContextMenu, { CONTEXT_MENU_ID } from './EmptyAreaContextMenu';

const SContainer = styled.div<{ width?: number }>`
  display: flex;
  flex-direction: column;
  height: calc(100% - 118px);
  overflow-y: auto;
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
`;

const STreeArea = styled.div`
  flex: 0 0 auto;
`;

const SEmptyArea = styled.div`
  height: 100%;
`;

interface Props {
  query?: string;
  data: ApiItem[];
  renderNoSearchResults: (query?: string) => ReactNode;
  onDoubleClickItem: (input: TreeItemType) => void;
  onUpload: (itemPath: Path) => void;
  width?: number;
}

const StaticTree = ({
  data,
  onDoubleClickItem,
  query,
  renderNoSearchResults,
  width,
  onUpload,
}: Props) => {
  const [
    { tree, filteredTree },
    {
      expand,
      filter,
      deleteNode,
      collapse,
      resetSearch,
      setEdit,
      rename,
      setTree,
    },
  ] = useTree(data);

  useEffect(() => {
    if (!query) {
      return resetSearch();
    }
    return filter(query);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [query]);

  // NOTE: The following is a *hack* in order to keep both the Tree state and
  // redux state in sync. This must be tackled as soon as possible.
  const updatedTree = normalize(data);
  if (
    tree &&
    updatedTree &&
    Object.keys(tree.items).length !== Object.keys(updatedTree.items).length
  ) {
    setTree(updatedTree);
  }

  // When `filteredTree.items` contains only 1 property, then that's the
  // internal root folder and we are sure that there are 0 search result.
  const hasZeroSearchResults =
    !!query && !!filteredTree && size(filteredTree.items) === 1;

  return (
    <TreeActionsProvider value={{ setEdit, rename, deleteNode }}>
      <SContainer width={width}>
        {hasZeroSearchResults ? (
          renderNoSearchResults(query)
        ) : (
          <>
            <STreeArea data-test="tree-area">
              <GenericTree
                query={query}
                renderItem={props => <TreeItem {...props} width={width} />}
                tree={filteredTree || tree}
                onCollapse={collapse}
                onDoubleClickItem={onDoubleClickItem}
                onExpand={expand}
                onUpload={onUpload}
              />
            </STreeArea>

            <ContextMenuTrigger
              attributes={{ style: { display: 'flex', flex: '1 1 auto' } }}
              id={CONTEXT_MENU_ID}
            >
              <SEmptyArea data-test={CONTEXT_MENU_ID} />
            </ContextMenuTrigger>
          </>
        )}
      </SContainer>

      <EmptySpaceContextMenu />
    </TreeActionsProvider>
  );
};

export default StaticTree;
